import type { UploadObservation } from './upload-capacity.js';
import type { IceCandidatePayload } from '@ohmyprime/p2p-shared';
import type { NetworkPath } from './network-accounting.js';

import { URGENT_DATACHANNEL_NAME, BULK_DATACHANNEL_NAME } from '@ohmyprime/p2p-shared';

const LEGACY_DATACHANNEL_NAME = 'segments'; // backward compat with old peers
const SEGMENT_ID_BYTES = 64;
/** Reserved request frame; never a media id or a catalog entry. Unknown to legacy peers. */
export const ICE_RESTART_REQUEST = '@ice-restart';
const FLAGS_BYTE = 1;
const CHUNK_HEADER = SEGMENT_ID_BYTES + FLAGS_BYTE;
/**
 * Conservative fallback chunk payload used when the SCTP maxMessageSize is not
 * available (old browser / non-standard env) or is 0 / non-finite.
 */
const MAX_CHUNK_PAYLOAD = 15_000;
/**
 * Upper cap for the negotiated SCTP payload.  The WebRTC spec mandates at least
 * 65_536 bytes on modern browsers; we honour that ceiling rather than an
 * arbitrarily large value, keeping individual send() calls predictable.
 */
const SCTP_PAYLOAD_CAP = 65_536;
/**
 * Buffer ceiling: enough for ~64 chunks at 64 KB = 4 MB.  Unchanged from the
 * previous constant; it is already suitable for large chunks.
 */
const MAX_BUFFERED_BYTES = 4_000_000;
/**
 * Low-watermark: must be ≥ one effective chunk so the drain loop is always
 * unblocked after the first chunk of a new (large) send.  At 64 KB per chunk
 * we need at least 65_536 + CHUNK_HEADER bytes.  We round up to 128 KB to
 * give the event loop a small batch window without stalling the sender.
 */
const BUFFERED_LOW_WATERMARK = 131_072; // 128 KB  (≥ 1 × SCTP_PAYLOAD_CAP)
const STATS_INTERVAL_MS = 5_000;

export const MAX_ASSEMBLED_SEGMENT_BYTES = 10_000_000; // 10 MB

/**
 * Pure helper — exported for unit testing.
 *
 * Given an RTCSctpTransport (or null/undefined), returns the effective per-chunk
 * payload size to use:
 *   min(maxMessageSize, SCTP_PAYLOAD_CAP) - CHUNK_HEADER
 *
 * Falls back to MAX_CHUNK_PAYLOAD when:
 *   - sctp is null / undefined
 *   - maxMessageSize is 0, NaN, or non-finite (browser quirk / spec deviation)
 */
export function computeSctpChunkPayload(
  sctp: { maxMessageSize: number } | null | undefined,
): number {
  if (!sctp) return MAX_CHUNK_PAYLOAD;
  const mms = sctp.maxMessageSize;
  if (typeof mms !== 'number' || !isFinite(mms) || mms <= 0) {
    return MAX_CHUNK_PAYLOAD;
  }
  // A maxMessageSize between 1 and CHUNK_HEADER yields a non-positive payload
  // (e.g. mms=1 → 1 - 65 = -64). A negative/zero payload would drive an infinite
  // loop and a negative-index subarray in sendNextSegmentChunk, so fall back.
  const payload = Math.min(mms, SCTP_PAYLOAD_CAP) - CHUNK_HEADER;
  return payload > 0 ? payload : MAX_CHUNK_PAYLOAD;
}

/*
 * DataChannel framing (reliable, ordered channel):
 *   request      [SEGMENT_ID (64)]                      exactly 64 bytes
 *   first chunk  [SEGMENT_ID (64)][FLAG_FIRST|FLAG_LAST][payload]
 *   next chunks  [FLAG_MORE|FLAG_LAST][payload]          while a transfer is active
 *   NACK         [SEGMENT_ID (64)][FLAG_NACK]            exactly 65 bytes: "I will not
 *                send this segment now"; the requester should try another source.
 * Flag values 0x04/0x05 were omp's live-head relay and are reserved; frames with
 * unknown flags are ignored.
 */
const FLAG_LAST = 0x00;
const FLAG_MORE = 0x01;
const FLAG_FIRST = 0x02;
export const FLAG_NACK = 0x06;
/** Receipt binds the object id to its verified ciphertext hash. Legacy peers ignore it. */
export const FLAG_RECEIPT = 0x07;

interface QueuedSegmentSend {
  segmentId: string;
  data: ArrayBuffer;
  offset: number;
  sha256?: string;
}

export type DataChannelMessageHandler = (
  segmentId: string,
  data: ArrayBuffer,
  fromPeerId: string,
) => void;

export type PeerStateChangeHandler = (
  peerId: string,
  state: RTCPeerConnectionState,
) => void;

export class PeerConnection {
  readonly peerId: string;

  private readonly pc: RTCPeerConnection;
  private dc: RTCDataChannel | null = null; // legacy / fallback
  private urgentDc: RTCDataChannel | null = null;
  private bulkDc: RTCDataChannel | null = null;
  private destroyed = false;

  private onMessage: DataChannelMessageHandler | null = null;
  private onRequest: ((segmentId: string, fromPeerId: string) => void) | null =
    null;
  private onStateChange: PeerStateChangeHandler | null = null;
  /** The remote peer declined to send a segment we requested (NACK frame). */
  onNack: ((segmentId: string, fromPeerId: string) => void) | null = null;

  /** Fired once when a reliable data channel is ready for segment requests. */
  onDataChannelReady: (() => void) | null = null;
  onIceRestartRequest: (() => void) | null = null;
  /** Payload counters run before assembly/verification; partial transfers still cost network. */
  onPayloadReceived: ((bytes: number) => void) | null = null;
  onPartialReceiveCanceled: ((bytes: number) => void) | null = null;
  private dataChannelReadyNotified = false;

  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteDescSet = false;

  private readonly chunkBuffers: Map<string, Uint8Array[]> = new Map();
  private activeTransferId: string | null = null;

  private readonly sendQueue: QueuedSegmentSend[] = [];
  private sendDrainActive = false;
  private sendDrainScheduled = false;
  private sendDrainTimer: ReturnType<typeof setTimeout> | null = null;
  private sendDrainPromise: Promise<void> = Promise.resolve();
  private readonly bufferWaiters = new Set<() => void>();
  private closePromise: Promise<void> | null = null;
  private readonly pendingWork = new Set<Promise<unknown>>();
  private readonly iceWaiters = new Set<() => void>();
  /** Lets the engine retain settlement ownership even after this peer leaves its map. */
  onWorkStarted?: (work: Promise<unknown>) => void;
  private trackWork<T>(work: Promise<T>): Promise<T> {
    this.pendingWork.add(work);
    this.onWorkStarted?.(work);
    void work.then(() => this.pendingWork.delete(work), () => this.pendingWork.delete(work));
    return work;
  }
  private waitForIceStability(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    return new Promise(resolve => {
      const finish = () => { clearTimeout(timer); this.iceWaiters.delete(finish); resolve(); };
      const timer = setTimeout(finish, 250);
      this.iceWaiters.add(finish);
    });
  }
  /**
   * Effective chunk payload size negotiated via SCTP maxMessageSize.
   * Computed once when the first data channel opens; falls back to
   * MAX_CHUNK_PAYLOAD if SCTP info is unavailable or invalid.
   */
  private negotiatedChunkPayload: number | null = null;

  private successCount = 0;
  private failureCount = 0;
  private totalBytesReceived = 0;
  private totalDurationMs = 0;
  private rttMs = 100;
  private readonly outboundCounters = new Map<string, number>();
  private outboundBytes = 0;
  private selectedPath: NetworkPath = 'unknown';
  private lastPathSampleAt = 0;
  get transportPath(): NetworkPath {
    return Date.now() - this.lastPathSampleAt <= STATS_INTERVAL_MS * 2 ? this.selectedPath : 'unknown';
  }
  onUploadMeasured?: (observation: UploadObservation) => void;
  uploadDeadlineMs = 1000;
  private confirmedBytes = 0;
  private confirmedDeliveries = 0;
  private readonly awaitingReceipts = new Map<string, { sha256: string; bytes: number; sentAt: number; complete: boolean; measured?: boolean }>();
  private outboundRate = 0;
  private lastOutboundSampleAt = Date.now();

  /** Browser-observed DataChannel bytes; queued data and reception are excluded. */
  get uploadSnapshot(): { bytesSent: number; bytesPerSecond: number; queuedBytes: number; confirmedBytes: number; confirmedDeliveries: number } {
    const now = Date.now();
    for (const [id, receipt] of this.awaitingReceipts) {
      if (!receipt.measured && now - receipt.sentAt > this.uploadDeadlineMs) {
        receipt.measured = true;
        if (id !== 'init.mp4') this.onUploadMeasured?.({startedAt: receipt.sentAt, finishedAt: now, bytes: receipt.bytes, delivered: false});
      }
    }
    const channels = new Set([this.dc, this.bulkDc, this.urgentDc]);
    let queuedBytes = this.sendQueue.reduce((n, item) => n + item.data.byteLength - item.offset, 0);
    for (const channel of channels) queuedBytes += channel?.bufferedAmount ?? 0;
    return {
      bytesSent: this.outboundBytes,
      confirmedBytes: this.confirmedBytes,
      confirmedDeliveries: this.confirmedDeliveries,
      bytesPerSecond: Date.now() - this.lastOutboundSampleAt <= STATS_INTERVAL_MS * 2 ? this.outboundRate : 0,
      queuedBytes,
    };
  }
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private originalOfferer = false;
  private iceRevision = 0;
  private lastIceRestartAt = -Infinity;
  private iceRestartTimer: ReturnType<typeof setTimeout> | null = null;
  private iceRestartRequestPending = false;

  constructor(
    peerId: string,
    iceServers: RTCIceServer[],
    iceTransportPolicy: RTCIceTransportPolicy = 'all',
    onIceCandidate: (peerId: string, candidate: IceCandidatePayload) => void,
    onMessage: DataChannelMessageHandler,
    onRequest: (segmentId: string, fromPeerId: string) => void,
    onStateChange: PeerStateChangeHandler,
  ) {
    this.peerId = peerId;
    this.onMessage = onMessage;
    this.onRequest = onRequest;
    this.onStateChange = onStateChange;

    this.pc = new RTCPeerConnection({ iceServers, iceTransportPolicy });

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        onIceCandidate(peerId, {
          candidate: event.candidate.candidate,
          sdpMid: event.candidate.sdpMid,
          sdpMLineIndex: event.candidate.sdpMLineIndex,
        });
      }
    };

    this.pc.onconnectionstatechange = () => {
      this.onStateChange?.(this.peerId, this.pc.connectionState);
    };

    this.pc.ondatachannel = (event) => {
      this.attachDataChannel(event.channel);
    };
  }

  createOffer(): Promise<RTCSessionDescriptionInit> {
    return this.trackWork(this.createOfferOwned());
  }
  private async createOfferOwned(): Promise<RTCSessionDescriptionInit> {
    this.originalOfferer = true;
    // Fase 2: create both prioritized channels (urgent first for live edge)
    const urgentDc = this.pc.createDataChannel(URGENT_DATACHANNEL_NAME, {
      ordered: false,
      maxRetransmits: 0,
    });
    this.attachDataChannel(urgentDc);

    const bulkDc = this.pc.createDataChannel(BULK_DATACHANNEL_NAME, {
      ordered: true,
    });
    this.attachDataChannel(bulkDc);

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  /** Renew relay allocations without replacing the SCTP association or its channels. */
  configureIceServers(iceServers: RTCIceServer[], iceTransportPolicy: RTCIceTransportPolicy): Promise<RTCSessionDescriptionInit | null> {
    return this.trackWork(this.configureIceServersOwned(iceServers, iceTransportPolicy));
  }
  private async configureIceServersOwned(iceServers: RTCIceServer[], iceTransportPolicy: RTCIceTransportPolicy): Promise<RTCSessionDescriptionInit | null> {
    if (this.destroyed) return null;
    ++this.iceRevision;
    this.pc.setConfiguration({ ...this.pc.getConfiguration(), iceServers, iceTransportPolicy });
    if (!this.originalOfferer) {
      this.iceRestartRequestPending = true;
      this.sendIceRestartRequest();
      return null;
    }
    return this.restartIce();
  }

  private sendIceRestartRequest(): void {
    if (!this.iceRestartRequestPending || this.destroyed) return;
    const channel = this.getReliableSendChannel();
    if (channel?.readyState !== 'open') return;
    try { channel.send(this.encodeSegmentId(ICE_RESTART_REQUEST)); this.iceRestartRequestPending = false; }
    catch { /* A channel opening later retries this one pending request. */ }
  }

  restartIce(): Promise<RTCSessionDescriptionInit | null> {
    return this.trackWork(this.restartIceOwned());
  }
  private async restartIceOwned(): Promise<RTCSessionDescriptionInit | null> {
    if (!this.originalOfferer || this.destroyed || !this.isOpen()) return null;
    const wait = 5000 - (Date.now() - this.lastIceRestartAt);
    if (wait > 0) {
      // Coalesce both sides refreshing together. The trailing offer uses the answerer's renewed allocation too.
      if (!this.iceRestartTimer) this.iceRestartTimer = setTimeout(() => {
        this.iceRestartTimer = null;
        if (!this.destroyed) this.onIceRestartRequest?.();
      }, wait);
      return null;
    }
    this.lastIceRestartAt = Date.now();
    const revision = this.iceRevision;
    // Only the original offerer renegotiates. A simultaneous refresh on the answerer cannot glare.
    for (let attempt = 0; this.pc.signalingState !== 'stable' && attempt < 4; attempt++) {
      await this.waitForIceStability();
      if (this.destroyed || revision !== this.iceRevision) return null;
    }
    if (this.destroyed || revision !== this.iceRevision) return null;
    if (this.pc.signalingState !== 'stable') {
      if (!this.iceRestartTimer) this.iceRestartTimer = setTimeout(() => {
        this.iceRestartTimer = null;
        if (!this.destroyed) this.onIceRestartRequest?.();
      }, 4000);
      return null;
    }
    const offer = await this.pc.createOffer({ iceRestart: true });
    if (this.destroyed || revision !== this.iceRevision) return null;
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  createAnswer(offerSdp: string): Promise<RTCSessionDescriptionInit> {
    return this.trackWork(this.createAnswerOwned(offerSdp));
  }
  private async createAnswerOwned(offerSdp: string): Promise<RTCSessionDescriptionInit> {
    await this.pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
    this.remoteDescSet = true;
    await this.flushPendingCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  acceptAnswer(answerSdp: string): Promise<void> {
    return this.trackWork(this.acceptAnswerOwned(answerSdp));
  }
  private async acceptAnswerOwned(answerSdp: string): Promise<void> {
    await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    this.remoteDescSet = true;
    await this.flushPendingCandidates();
  }

  addIceCandidate(payload: IceCandidatePayload): Promise<void> {
    return this.trackWork(this.addIceCandidateOwned(payload));
  }
  private async addIceCandidateOwned(payload: IceCandidatePayload): Promise<void> {
    const init: RTCIceCandidateInit = {
      candidate: payload.candidate,
      sdpMid: payload.sdpMid,
      sdpMLineIndex: payload.sdpMLineIndex,
    };
    if (this.remoteDescSet) {
      await this.pc.addIceCandidate(init);
    } else {
      this.pendingCandidates.push(init);
    }
  }

  requestSegment(segmentId: string, urgent = true): boolean {
    // Segment requests are control messages: they must arrive reliably.
    // The response may be latency-sensitive, but losing this tiny request means
    // the peer never queues/fetches the segment at all.
    const dc = this.getReliableSendChannel();
    if (!dc || dc.readyState !== 'open') return false;
    dc.send(this.encodeSegmentId(segmentId));
    return true;
  }

  /** Tell the remote peer we will not send `segmentId` now (it should try another source). */
  sendNack(segmentId: string): boolean {
    const dc = this.getReliableSendChannel();
    if (!dc || dc.readyState !== 'open') return false;
    const out = new Uint8Array(CHUNK_HEADER);
    out.set(new Uint8Array(this.encodeSegmentId(segmentId)), 0);
    out[SEGMENT_ID_BYTES] = FLAG_NACK;
    try {
      dc.send(out.buffer);
      return true;
    } catch {
      return false;
    }
  }

  /** Segments queued for sending to this peer and not fully sent yet. */
  pendingSendCount(): number {
    return this.sendQueue.length;
  }

  /** Queue a full segment for delivery; drains when the channel buffer has space. */
  sendSegment(segmentId: string, data: ArrayBuffer, urgent = false, sha256?: string): boolean {
    // Full m4s segments are chunked and must be delivered in order. Use the
    // reliable ordered channel; the unreliable urgent channel would lose chunks.
    const dc = this.getReliableSendChannel();
    if (!dc || dc.readyState !== 'open') return false;
    if (sha256 && /^[a-f0-9]{64}$/i.test(sha256)) {
      for (const [id, receipt] of this.awaitingReceipts) {
        if (Date.now() - receipt.sentAt > 30_000) this.awaitingReceipts.delete(id);
      }
      this.awaitingReceipts.delete(segmentId);
      while (this.awaitingReceipts.size >= 128) this.awaitingReceipts.delete(this.awaitingReceipts.keys().next().value!);
      this.awaitingReceipts.set(segmentId, { sha256: sha256.toLowerCase(), bytes: data.byteLength, sentAt: Date.now(), complete: false });
    }
    this.sendQueue.push({ segmentId, data, offset: 0, sha256: sha256?.toLowerCase() });
    this.scheduleDrainSendQueue();
    return true;
  }

  /** Remove obsolete unsent objects without truncating the ordered wire transfer. */
  invalidateQueuedSends(): void {
    // Continuations carry no id: an already-started object must finish before
    // the replacement. Its old hash cannot acknowledge the new receipt entry.
    for (let i = this.sendQueue.length - 1; i >= 0; i--) {
      if (this.sendQueue[i]!.offset === 0) this.sendQueue.splice(i, 1);
    }
    this.awaitingReceipts.clear();
  }

  /** Called after the receiver verifies ciphertext against its authorized catalog. */
  confirmSegmentReceived(segmentId: string, sha256: string): void {
    const dc = this.getReliableSendChannel();
    if (!dc || dc.readyState !== 'open' || !/^[a-f0-9]{64}$/i.test(sha256)) return;
    const receipt = new Uint8Array(CHUNK_HEADER + 32);
    receipt.set(new Uint8Array(this.encodeSegmentId(segmentId)));
    receipt[SEGMENT_ID_BYTES] = FLAG_RECEIPT;
    for (let i = 0; i < 32; i++) receipt[CHUNK_HEADER + i] = parseInt(sha256.slice(i * 2, i * 2 + 2), 16);
    try { dc.send(receipt.buffer); } catch { /* An unavailable receipt cannot imply successful delivery. */ }
  }

  isOpen(): boolean {
    return this.pc.connectionState === 'connected' && this.getReliableSendChannel() !== null;
  }

  get reliability(): number {
    const total = this.successCount + this.failureCount;
    if (total === 0) return 1;
    return this.successCount / total;
  }

  get avgSpeedBytesPerSec(): number {
    if (this.totalDurationMs === 0) return 0;
    return (this.totalBytesReceived / this.totalDurationMs) * 1000;
  }

  get measuredRttMs(): number {
    return this.rttMs;
  }

  recordSuccess(bytesReceived: number, durationMs: number): void {
    this.successCount++;
    this.totalBytesReceived += bytesReceived;
    this.totalDurationMs += durationMs;
  }

  recordFailure(): void {
    this.failureCount++;
  }

  close(): Promise<void> {
    if (this.closePromise) return this.closePromise;
    this.destroy();
    this.closePromise = (async () => {
      await this.sendDrainPromise;
      while (this.pendingWork.size) await Promise.allSettled([...this.pendingWork]);
    })();
    return this.closePromise;
  }
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.onWorkStarted = undefined;
    for (const finish of this.iceWaiters) finish();
    this.iceWaiters.clear();
    if (this.sendDrainTimer) clearTimeout(this.sendDrainTimer);
    this.sendDrainTimer = null; this.sendDrainScheduled = false;
    for (const finish of this.bufferWaiters) finish();
    this.bufferWaiters.clear();
    if (this.iceRestartTimer) clearTimeout(this.iceRestartTimer);
    this.iceRestartTimer = null;
    this.onIceRestartRequest = null;
    this.sendQueue.length = 0;
    this.awaitingReceipts.clear();
    let partialBytes = 0;
    for (const chunks of this.chunkBuffers.values()) for (const chunk of chunks) partialBytes += chunk.byteLength;
    if (partialBytes) this.onPartialReceiveCanceled?.(partialBytes);
    this.chunkBuffers.clear();
    this.onPayloadReceived = null;
    this.onPartialReceiveCanceled = null;
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
    this.onMessage = null;
    this.onRequest = null;
    this.onStateChange = null;
    this.onNack = null;
    if (this.dc) {
      this.dc.close();
      this.dc = null;
    }
    this.pc.close();
  }

  private attachDataChannel(dc: RTCDataChannel): void {
    dc.binaryType = 'arraybuffer';
    dc.bufferedAmountLowThreshold = BUFFERED_LOW_WATERMARK;

    const label = dc.label;
    if (label === URGENT_DATACHANNEL_NAME) {
      this.urgentDc = dc;
    } else if (label === BULK_DATACHANNEL_NAME) {
      this.bulkDc = dc;
    } else if (label === LEGACY_DATACHANNEL_NAME) {
      this.dc = dc; // legacy single-channel peers
    } else {
      // Unknown label — treat as bulk fallback
      this.bulkDc = dc;
    }

    dc.onopen = () => {
      // Compute the negotiated chunk payload once, on first channel open.
      if (this.negotiatedChunkPayload === null) {
        this.negotiatedChunkPayload = this.computeNegotiatedChunkPayload();
      }
      this.startStatsPolling();
      this.scheduleDrainSendQueue();
      this.notifyDataChannelReadyIfNeeded();
      this.sendIceRestartRequest();
    };

    dc.onclose = () => {
      // SCTP closes before ICE notices a remote teardown. Keeping this connection
      // until the ICE timeout prevents the engine from replacing a dead media path.
      // An urgent-channel loss is harmless while reliable bulk/legacy still works.
      if (!this.destroyed && (dc === this.bulkDc || dc === this.dc) && !this.getReliableSendChannel()) {
        this.onStateChange?.(this.peerId, 'disconnected');
      }
    };

    dc.onbufferedamountlow = () => {
      this.scheduleDrainSendQueue();
    };

    dc.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (this.destroyed) return;
      const buf = event.data;
      if (buf.byteLength === 0) return;

      const firstByte = new Uint8Array(buf, 0, 1)[0]!;

      // Continuation chunk: FLAG_MORE or FLAG_LAST while a transfer is active.
      // The previous check incorrectly required byteLength < CHUNK_HEADER for
      // FLAG_LAST, causing the last chunk (always >= 65 bytes) to be misparsed
      // as a new first chunk — silently breaking every P2P segment delivery.
      if (
        this.activeTransferId !== null &&
        (firstByte === FLAG_MORE || firstByte === FLAG_LAST)
      ) {
        const segmentId = this.activeTransferId;
        const chunkData = new Uint8Array(buf.slice(1));
        this.pushChunk(segmentId, chunkData, firstByte === FLAG_LAST);
        return;
      }

      if (buf.byteLength === SEGMENT_ID_BYTES) {
        const idBytes = new Uint8Array(buf, 0, SEGMENT_ID_BYTES);
        const segmentId = new TextDecoder()
          .decode(idBytes)
          .replace(/\0+$/, '');
        if (segmentId === ICE_RESTART_REQUEST) {
          if (this.originalOfferer) this.onIceRestartRequest?.();
          return;
        }
        this.onRequest?.(segmentId, this.peerId);
        return;
      }

      if (buf.byteLength < CHUNK_HEADER) return;

      const idBytes = new Uint8Array(buf, 0, SEGMENT_ID_BYTES);
      const segmentId = new TextDecoder()
        .decode(idBytes)
        .replace(/\0+$/, '');

      const flags = new Uint8Array(buf, SEGMENT_ID_BYTES, 1)[0]!;
      const chunkData = new Uint8Array(buf.slice(CHUNK_HEADER));

      if (flags === FLAG_RECEIPT) {
        if (buf.byteLength !== CHUNK_HEADER + 32) return;
        const receipt = this.awaitingReceipts.get(segmentId);
        const sha256 = Array.from(chunkData, b => b.toString(16).padStart(2, '0')).join('');
        if (receipt?.complete && receipt.sha256 === sha256 && Date.now() - receipt.sentAt <= 30_000) {
          this.awaitingReceipts.delete(segmentId);
          this.confirmedBytes += receipt.bytes;
          this.confirmedDeliveries++;
          if (!receipt.measured && segmentId !== 'init.mp4') {
            this.onUploadMeasured?.({startedAt: receipt.sentAt, finishedAt: Date.now(), bytes: receipt.bytes,
              delivered: Date.now() - receipt.sentAt <= this.uploadDeadlineMs});
          }
        }
        return;
      }
      if (flags === FLAG_NACK) {
        this.onNack?.(segmentId, this.peerId);
        return;
      }
      if (flags !== FLAG_FIRST && flags !== FLAG_LAST && flags !== FLAG_MORE) return;

      this.activeTransferId = segmentId;
      this.pushChunk(segmentId, chunkData, flags === FLAG_LAST);
    };
  }

  private async drainSendQueue(): Promise<void> {
    if (this.sendDrainActive || this.destroyed) return;
    this.sendDrainActive = true;

    try {
      while (this.sendQueue.length > 0 && !this.destroyed) {
        const dc = this.getReliableSendChannel();
        if (!dc || dc.readyState !== 'open') break;

        if (dc.bufferedAmount > MAX_BUFFERED_BYTES) {
          await this.waitForBufferLow(dc);
          continue;
        }

        const item = this.sendQueue[0]!;
        const sent = this.sendNextSegmentChunk(dc, item);
        if (!sent) {
          await this.waitForBufferLow(dc);
          continue;
        }
        if (item.offset >= item.data.byteLength) {
          this.sendQueue.shift();
        }
      }
    } finally {
      this.sendDrainActive = false;
    }
  }

  private scheduleDrainSendQueue(): void {
    if (this.sendDrainScheduled || this.sendDrainActive || this.destroyed) return;
    this.sendDrainScheduled = true;
    this.sendDrainTimer = setTimeout(() => {
      this.sendDrainTimer = null;
      this.sendDrainScheduled = false;
      this.sendDrainPromise = this.trackWork(this.drainSendQueue()).catch((err) => {
        console.warn('[peer-connection] drainSendQueue failed:', err);
      });
    }, 0);
  }

  private waitForBufferLow(dc: RTCDataChannel | null): Promise<void> {
    if (!dc || dc.bufferedAmount <= BUFFERED_LOW_WATERMARK) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.bufferWaiters.delete(finish);
        dc.removeEventListener('bufferedamountlow', onLow);
        clearTimeout(timer);
        resolve();
      };
      const onLow = () => finish();
      const timer = setTimeout(finish, 2_000);
      this.bufferWaiters.add(finish);
      dc.addEventListener('bufferedamountlow', onLow);
    });
  }

  /** Fase 2: prefer urgent channel for live-edge traffic when available */
  private getPreferredSendChannel(preferUrgent = false): RTCDataChannel | null {
    if (preferUrgent && this.urgentDc && this.urgentDc.readyState === 'open') {
      return this.urgentDc;
    }
    if (this.urgentDc && this.urgentDc.readyState === 'open') return this.urgentDc;
    if (this.bulkDc && this.bulkDc.readyState === 'open') return this.bulkDc;
    return this.dc && this.dc.readyState === 'open' ? this.dc : null;
  }

  private getReliableSendChannel(): RTCDataChannel | null {
    if (this.bulkDc && this.bulkDc.readyState === 'open') return this.bulkDc;
    if (this.dc && this.dc.readyState === 'open') return this.dc;
    return null;
  }

  private sendNextSegmentChunk(
    dc: RTCDataChannel,
    item: QueuedSegmentSend,
  ): boolean {
    if (!dc || dc.readyState !== 'open') return false;

    const idBytes = new Uint8Array(SEGMENT_ID_BYTES);
    const encoded = new TextEncoder().encode(item.segmentId);
    idBytes.set(encoded.slice(0, SEGMENT_ID_BYTES));

    const src = new Uint8Array(item.data);
    const offset = item.offset;
    if (offset >= src.byteLength) return true;
    if (dc.bufferedAmount > MAX_BUFFERED_BYTES) return false;

    const remaining = src.byteLength - offset;
    const effectivePayload = this.negotiatedChunkPayload ?? MAX_CHUNK_PAYLOAD;
    const chunkSize = Math.min(remaining, effectivePayload);
    const isFirst = offset === 0;
    const isLast = offset + chunkSize >= src.byteLength;

    try {
      if (isFirst) {
        const out = new Uint8Array(CHUNK_HEADER + chunkSize);
        out.set(idBytes, 0);
        out[SEGMENT_ID_BYTES] = isLast ? FLAG_LAST : FLAG_FIRST;
        out.set(src.subarray(offset, offset + chunkSize), CHUNK_HEADER);
        dc.send(out.buffer);
      } else {
        const out = new Uint8Array(1 + chunkSize);
        out[0] = isLast ? FLAG_LAST : FLAG_MORE;
        out.set(src.subarray(offset, offset + chunkSize), 1);
        dc.send(out.buffer);
      }

      item.offset += chunkSize;
      if (isLast) {
        const receipt = this.awaitingReceipts.get(item.segmentId);
        if (receipt && receipt.sha256 === item.sha256) receipt.complete = true;
      }
      return true;
    } catch (err) {
      console.warn(
        `[peer-connection] sendSegment failed for ${item.segmentId}:`,
        err,
      );
      return false;
    }
  }

  /**
   * Read pc.sctp?.maxMessageSize (the minimum of the two peers' SCTP caps as
   * negotiated during DTLS/SCTP handshake) and derive a safe per-connection
   * chunk payload size.  Delegates to the exported pure helper.
   */
  private computeNegotiatedChunkPayload(): number {
    const sctp = (this.pc as RTCPeerConnection & { sctp?: { maxMessageSize: number } | null }).sctp;
    return computeSctpChunkPayload(sctp);
  }

  private pushChunk(
    segmentId: string,
    chunkData: Uint8Array,
    isLast: boolean,
  ): void {
    this.onPayloadReceived?.(chunkData.byteLength);
    let chunks = this.chunkBuffers.get(segmentId);
    if (!chunks) {
      chunks = [];
      this.chunkBuffers.set(segmentId, chunks);
    }
    chunks.push(chunkData);

    const totalReceived = chunks.reduce((s, c) => s + c.byteLength, 0);
    if (totalReceived > MAX_ASSEMBLED_SEGMENT_BYTES) {
      this.chunkBuffers.delete(segmentId);
      this.activeTransferId = null;
      console.warn(`[peer-connection] segment ${segmentId} exceeded max size, dropping`);
      return;
    }

    if (isLast) {
      this.chunkBuffers.delete(segmentId);
      this.activeTransferId = null;
      const totalSize = chunks.reduce((s, c) => s + c.byteLength, 0);
      const assembled = new Uint8Array(totalSize);
      let offset = 0;
      for (const chunk of chunks) {
        assembled.set(chunk, offset);
        offset += chunk.byteLength;
      }
      this.onMessage?.(segmentId, assembled.buffer, this.peerId);
    }
  }

  private notifyDataChannelReadyIfNeeded(): void {
    if (this.dataChannelReadyNotified || this.destroyed) return;
    const dc = this.getReliableSendChannel();
    if (!dc || dc.readyState !== 'open') return;
    this.dataChannelReadyNotified = true;
    this.onDataChannelReady?.();
  }

  private encodeSegmentId(segmentId: string): ArrayBuffer {
    const buf = new Uint8Array(SEGMENT_ID_BYTES);
    const encoded = new TextEncoder().encode(segmentId);
    buf.set(encoded.slice(0, SEGMENT_ID_BYTES));
    return buf.buffer;
  }

  private async flushPendingCandidates(): Promise<void> {
    for (const c of this.pendingCandidates) {
      await this.pc.addIceCandidate(c);
    }
    this.pendingCandidates.length = 0;
  }

  private startStatsPolling(): void {
    if (this.statsTimer) return;
    this.statsTimer = setInterval(() => {
      void this.pollStats();
    }, STATS_INTERVAL_MS);
  }

  private pollStats(): Promise<void> {
    return this.trackWork(this.pollStatsOwned());
  }
  private async pollStatsOwned(): Promise<void> {
    if (this.destroyed) return;
    try {
      const stats = await this.pc.getStats();
      if (this.destroyed) return;
      const now = Date.now();
      let sent = 0;
      let hasOutboundSample = false;
      stats.forEach((report) => {
        if (report.type === 'data-channel') {
          const bytes = (report as RTCStats & { bytesSent?: number }).bytesSent;
          if (typeof bytes === 'number' && Number.isFinite(bytes) && bytes >= 0) {
            const previous = this.outboundCounters.get(report.id) ?? 0;
            // A reset/recreated channel starts a new counter, never negative traffic.
            sent += bytes >= previous ? bytes - previous : bytes;
            this.outboundCounters.set(report.id, bytes);
            hasOutboundSample = true;
          }
        }
      });
      // Only the selected pair carries this connection's media. Succeeded checks may be unused.
      let selected: RTCStats & { localCandidateId?: string; remoteCandidateId?: string; currentRoundTripTime?: number } | undefined;
      stats.forEach(report => {
        if (report.type !== 'transport') return;
        const id = (report as RTCStats & { selectedCandidatePairId?: string }).selectedCandidatePairId;
        if (id) selected = stats.get(id);
      });
      const local = selected?.localCandidateId ? stats.get(selected.localCandidateId) as (RTCStats & { candidateType?: string }) | undefined : undefined;
      const remote = selected?.remoteCandidateId ? stats.get(selected.remoteCandidateId) as (RTCStats & { candidateType?: string }) | undefined : undefined;
      this.selectedPath = local?.candidateType === 'relay' || remote?.candidateType === 'relay' ? 'turn' :
        ['host', 'srflx', 'prflx'].includes(local?.candidateType ?? '') && ['host', 'srflx', 'prflx'].includes(remote?.candidateType ?? '') ? 'direct' : 'unknown';
      this.lastPathSampleAt = now;
      if (typeof selected?.currentRoundTripTime === 'number' && selected.currentRoundTripTime > 0) this.rttMs = Math.round(selected.currentRoundTripTime * 1000);
      if (hasOutboundSample) {
        const elapsed = now - this.lastOutboundSampleAt;
        this.outboundBytes += sent;
        this.outboundRate = elapsed > 0 ? sent * 1000 / elapsed : 0;
        this.lastOutboundSampleAt = now;
      }
    } catch {
      // Missing stats stay unknown; they cannot infer upload from downloads.
    }
  }
}
